// Deterministic, DOM-free transformations applied to externally-authored composition HTML before
// it loads into the render iframe (currently: inject the template-color palette as CSS variables).

export const TEMPLATE_COLOR_STYLE_ID = 'l5-template-colors';

export const GSAP_SCRIPT_ID = 'l5-gsap';

/** The pinned gsap build injected into every composition: deterministic version, CSP-allowlisted CDN. */
export const GSAP_CDN_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/gsap.min.js';

/** Prefix of the injected palette custom properties: `<prefix><index>` (e.g. `--l5-color-0`), index matching the template `colors` array. */
export const TEMPLATE_COLOR_VAR_PREFIX = '--l5-color-';

/** The `var(...)` reference an author uses to read template color `index` — the canonical API rather than hardcoding the property name. */
export function htmlTemplateColorVar(index: number): string {
    return `var(${TEMPLATE_COLOR_VAR_PREFIX}${index})`;
}

export function templateColorToHex(color: number): string {
    return '#' + ((color >>> 0) & 0xffffff).toString(16).padStart(6, '0');
}

export function buildTemplateColorCss(palette: string[]): string {
    const declarations = palette
        .map((color, index) => `${TEMPLATE_COLOR_VAR_PREFIX}${index}: ${color};`)
        .join(' ');
    return `:root { ${declarations} }`;
}

/** Insert or replace the template-color `<style>` block — idempotent (swaps rather than appends). */
export function injectTemplateColorVariables(html: string, palette: string[]): string {
    const styleTag = `<style id="${TEMPLATE_COLOR_STYLE_ID}">${buildTemplateColorCss(palette)}</style>`;

    const existingBlock = new RegExp(
        `<style id="${TEMPLATE_COLOR_STYLE_ID}">[\\s\\S]*?</style>`,
        'i',
    );
    const withoutExisting = html.replace(existingBlock, '');

    if (/<\/head>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<\/head>/i, `${styleTag}</head>`);
    }
    if (/<head[^>]*>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<head[^>]*>/i, (match) => `${match}${styleTag}`);
    }
    return `${styleTag}${withoutExisting}`;
}

export function hasTimelineContract(html: string): boolean {
    return /__timelines/.test(html);
}

/**
 * Inject the pinned gsap `<script>` at the start of `<head>` so it runs before the composition's
 * scripts; strips any author-included gsap tag so the pinned build wins (and stays idempotent).
 */
export function injectGsap(html: string): string {
    // Assemble the closing tag from parts so this source contains no literal closing-script
    // sequence, which would otherwise end an inline `<script>` block early when the luminary bundle
    // is embedded inline. The runtime string is unchanged.
    const closingScriptTag = '</' + 'script>';
    const scriptTag = `<script id="${GSAP_SCRIPT_ID}" src="${GSAP_CDN_URL}">${closingScriptTag}`;

    const existingGsapScript = /<script\b[^>]*\bsrc="[^"]*gsap[^"]*"[^>]*>\s*<\/script>/gi;
    const withoutExisting = html.replace(existingGsapScript, '');

    if (/<head[^>]*>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<head[^>]*>/i, (match) => `${match}${scriptTag}`);
    }
    if (/<\/head>/i.test(withoutExisting)) {
        return withoutExisting.replace(/<\/head>/i, `${scriptTag}</head>`);
    }
    return `${scriptTag}${withoutExisting}`;
}

/** Apply all auto-management transformations, producing the HTML loaded into the iframe. */
export function manageComposition(html: string, palette: string[]): string {
    return injectTemplateColorVariables(injectGsap(html), palette);
}
